import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getComponentSortRank } from '@/types/database';

export interface OrderComponent {
  sku: string;
  type: string;
  typeLabel: string;
  description: string;
  /** Customer-facing display name (from component_display_names) */
  displayName?: string | null;
  qty: number;
  unit_cost: number;
  line_total: number;
}

const TYPE_LABELS: Record<string, string> = {
  cpu: 'CPU', cpu_cooler: 'CPU Cooler', fan: 'CPU Cooler', coo: 'CPU Cooler',
  mb: 'Motherboard', ram: 'RAM',
  rom_1: 'Storage', rom_2: 'Storage', rom_3: 'Storage', storage: 'Storage', rom: 'Storage',
  gpu: 'GPU', psu: 'PSU', os: 'OS', case: 'Case', cas: 'Case',
};

function typeLabel(t: string): string {
  const lower = t?.toLowerCase() ?? '';
  if (TYPE_LABELS[lower]) return TYPE_LABELS[lower];
  if (lower.startsWith('acc')) return 'Accessory';
  return t || '—';
}

interface Order {
  id?: string;
  build?: { id?: string } | null;
  reservation?: { id?: string } | null;
  // Raw FK columns — used as a fallback when the joined object isn't materialised
  build_id?: string | null;
  reservation_id?: string | null;
}

/**
 * Fetches components for an assembly order using a 3-step fallback:
 * 1. assembly_order_components (order-specific snapshot — always populated for new orders via DB trigger)
 * 2. reservation_components (if order is linked to a reservation)
 * 3. build_components BOM (fallback for legacy orders with no snapshot)
 *
 * Both the joined relation (`order.reservation.id`) and the raw FK column
 * (`order.reservation_id`) are accepted, since not every caller passes the
 * join. Same for build.
 */
export function useOrderComponents(order: Order | null) {
  const reservationId = order?.reservation?.id || order?.reservation_id || null;
  const buildId = order?.build?.id || order?.build_id || null;

  return useQuery({
    queryKey: ['order-components', order?.id, reservationId, buildId],
    queryFn: async (): Promise<OrderComponent[]> => {
      let rows: Omit<OrderComponent, 'description' | 'typeLabel'>[] = [];

      // 1. assembly_order_components — most specific, always present for new orders
      if (order?.id) {
        const { data } = await supabase
          .from('assembly_order_components')
          .select('component_sku, component_type, component_qty, cost_unit_ex_vat')
          .eq('ao_id', order.id);
        if (data?.length) {
          rows = data.map(c => ({
            sku: c.component_sku,
            type: c.component_type || '—',
            qty: parseFloat(c.component_qty) || 1,
            unit_cost: parseFloat(c.cost_unit_ex_vat) || 0,
            line_total: (parseFloat(c.component_qty) || 1) * (parseFloat(c.cost_unit_ex_vat) || 0),
          }));
        }
      }

      // 2. reservation_components — reservation-level snapshot
      if (!rows.length && reservationId) {
        const { data } = await supabase
          .from('reservation_components')
          .select('component_sku, component_type, quantity, unit_cost')
          .eq('reservation_id', reservationId);
        if (data?.length) {
          rows = data.map(c => ({
            sku: c.component_sku,
            type: c.component_type || '—',
            qty: parseFloat(c.quantity) || 1,
            unit_cost: parseFloat(c.unit_cost) || 0,
            line_total: (parseFloat(c.quantity) || 1) * (parseFloat(c.unit_cost) || 0),
          }));
        }
      }

      // 3. build_components BOM — fallback for legacy orders
      if (!rows.length && buildId) {
        const { data } = await supabase
          .from('build_components')
          .select('component_sku, component_type, qty_per_build, cost_ex_vat')
          .eq('build_id', buildId)
          .order('component_type');
        if (data?.length) {
          rows = data.map(c => ({
            sku: c.component_sku,
            type: c.component_type || '—',
            qty: parseFloat(c.qty_per_build) || 1,
            unit_cost: parseFloat(c.cost_ex_vat) || 0,
            line_total: (parseFloat(c.qty_per_build) || 1) * (parseFloat(c.cost_ex_vat) || 0),
          }));
        }
      }

      if (!rows.length) return [];

      // Fetch descriptions and display names from components table
      const skus = rows.map(r => r.sku).filter(Boolean);
      const descMap: Record<string, string> = {};
      const displayNameMap: Record<string, string | null> = {};
      if (skus.length) {
        const [{ data: compData }, { data: dnData }] = await Promise.all([
          supabase
            .from('components')
            .select('part_number, description')
            .in('part_number', skus),
          supabase
            .from('component_display_names')
            .select('part_number, display_name_b2c')
            .in('part_number', skus),
        ]);
        if (compData) {
          for (const c of compData) {
            descMap[c.part_number] = c.description || '';
          }
        }
        if (dnData) {
          for (const d of dnData) {
            displayNameMap[d.part_number] = d.display_name_b2c || null;
          }
        }
      }

      // Sort by component type in standard build order
      const typeRank = (t: string) => getComponentSortRank(t ?? '');

      return rows
        .map(r => ({
          ...r,
          description: descMap[r.sku] || '',
          displayName: displayNameMap[r.sku] ?? null,
          typeLabel: typeLabel(r.type),
        }))
        .sort((a, b) => typeRank(a.type) - typeRank(b.type));
    },
    enabled: !!(order?.id || reservationId || buildId),
  });
}
